import React, { useState, useEffect } from 'react';
import { Collapse, Empty, Modal, Spin, Tag, Select, Space, Row, Col, Card, Button, message, Typography } from 'antd';
import { SaveOutlined, CopyOutlined, PlusOutlined, PictureOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import html2canvas from 'html2canvas'; // ใช้ในการแคปภาพลงเครื่อง

import UploadIMGPOST from './UploadIMGPOST';
import { baseUrlAREA, CHECK_THAI_POST, HEADERS_BASIC } from '../../../API/apiUrlsNew';
import TrackingDetail from '../thaipost/TrackingDetail';

dayjs.extend(buddhistEra);
dayjs.extend(customParseFormat);
dayjs.locale('th');

const { Panel } = Collapse;
const { Title } = Typography;

// 1. เพิ่มสถานะ '000' เข้าไปใน LABELS
const STATUS_LABELS = {
    'all': 'ทั้งหมด',
    '000': 'ไม่พบข้อมูล', // เพิ่มตรงนี้
    '101': 'เตรียมการฝากส่ง', '102': 'รับฝากผ่านตัวแทน', '103': 'รับฝาก',
    '104': 'ยกเลิกการรับฝาก', '201': 'ออกจากที่ทำการ', '202': 'ดำเนินพิธีการศุลกากร',
    '203': 'ส่งคืนต้นทาง', '204': 'ถึงที่ทำการแลกเปลี่ยนขาออก', '205': 'ถึงที่ทำการแลกเปลี่ยนขาเข้า',
    '206': 'ถึงที่ทำการไปรษณีย์', '208': 'ส่งออกจากที่ทำการแลกเปลี่ยนขาออก',
    '211': 'รับเข้า ณ ศูนย์คัดแยก', '220': 'ถึงที่ทำการ/ศูนย์ไปรษณีย์',
    '301': 'อยู่ระหว่างการนำจ่าย', '302': 'นำจ่าย ณ จุดรับสิ่งของ',
    '401': 'นำจ่ายไม่สำเร็จ', '501': 'นำจ่ายสำเร็จ', '901': 'โอนเงินเรียบร้อย'
};

const chunkArray = (array, size) => {
    if (!array || !Array.isArray(array)) return [];
    const chunked = [];
    for (let i = 0; i < array.length; i += size) {
        chunked.push(array.slice(i, i + size));
    }
    return chunked;
};

function CheckThaiPost({ open, close, data }) {
    const [loading, setLoading] = useState(false);
    const [parcelData, setParcelData] = useState([]);
    const [isModalUploadImg, setIsModalUploadImg] = useState(false);
    const [modalUploadImgData, setModalUploadImgData] = useState();
    const [filterStatus, setFilterStatus] = useState('all');

    const [chunkedData, setChunkedData] = useState({
        parcelChunks: [],
    });

    useEffect(() => {
        if (open && data?.length > 0) {
            onCheckThaiPost(data);
        }
    }, [open, data]);

    const handleClose = () => {
        setParcelData([]);
        setFilterStatus('all');
        setChunkedData({ parcelChunks: [] });
        close(false);
    };

    const getStepIndex = (status) => {
        if (status === "501" || status === "401") return 3;
        if (status === "301" || status === "302") return 2;
        if (status >= "201") return 1;
        return 0;
    };

    const onCheckThaiPost = (values) => {
        const validBarcodes = values
            .map(item => item.parcelNo)
            .filter(no => no !== null && no !== undefined && no.trim() !== "");

        if (validBarcodes.length === 0) {
            setParcelData([]);
            return;
        }

        setLoading(true);
        const sendData = { barcode: validBarcodes };

        axios.post(baseUrlAREA + CHECK_THAI_POST, sendData, { headers: HEADERS_BASIC })
            .then((response) => {
                const items = response.data.response.items;
                const formatted = data.map(item => {
                    const trackingInfo = items[item.parcelNo] || [];
                    const lastStatus = trackingInfo[trackingInfo.length - 1] || {};
                    // console.log("trackingInfo", trackingInfo)

                    // ถ้าไม่พบข้อมูล trackingInfo จะเป็น [] และ lastStatus เป็น {}
                    // ดังนั้น statusCode จะเป็น '000'
                    return {
                        ...item,
                        history: [...trackingInfo].reverse(),
                        currentStep: getStepIndex(lastStatus.status),
                        statusCode: lastStatus.status || '000',
                        statusDescription: lastStatus.status_description || 'ไม่พบข้อมูล',
                    };
                });
                setParcelData(formatted);
            })
            .catch(err => {
                console.error(err);
                message.error('เกิดข้อผิดพลาดในการดึงข้อมูล');
                const fallback = data.map(item => ({
                    ...item,
                    history: [],
                    currentStep: 0,
                    statusCode: '000',
                    statusDescription: 'ไม่พบข้อมูล',
                }));
                setParcelData(fallback);
            })
            .finally(() => setLoading(false));
    };

    // 2. แก้ไข activeData: เอา .filter ออก เพื่อให้แสดงรายการที่ statusCode === '000' ด้วย
    // const activeData = parcelData.filter(item => item.statusCode !== "000"); <--- ของเดิม
    const activeData = parcelData; // <--- ของใหม่ ใช้ข้อมูลทั้งหมดเลย

    const summary = activeData.reduce((acc, curr) => {
        acc[curr.statusCode] = (acc[curr.statusCode] || 0) + 1;
        return acc;
    }, { all: activeData.length });

    const filteredData = activeData.filter(item =>
        filterStatus === 'all' || item.statusCode === filterStatus
    );

    const handleSave = () => {
        if (!filteredData || filteredData.length === 0) return;

        const selectedParcels = filteredData.reduce((acc, curr) => {
            const history = curr?.history || [];
            const has401 = history.some(h => h.status === '401');
            const isLatest501 = history[0]?.status === '501';

            if (isLatest501) {
                const targetNo = has401 ? curr.parcelNo : curr.parcelNoResponse;
                if (targetNo) {
                    acc.push(targetNo);
                }
            }
            return acc;
        }, []);

        const chunkSize = 300;
        const chunks = chunkArray(selectedParcels, chunkSize);

        setChunkedData({
            parcelChunks: chunks
        });
    };

    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        message.success(`คัดลอก ${label} เรียบร้อย`);
    };

    const handleUploadIMGPOST = (value) => {
        setModalUploadImgData(value)
        setIsModalUploadImg(true)
    };

    // Helper สำหรับเลือกสี Tag
    const getStatusColor = (code) => {
        if (code === '501') return 'green';
        if (code === '401') return 'red';
        if (code === '000') return 'default'; // สีเทาสำหรับไม่พบข้อมูล
        return 'orange';
    };

    const handleCaptureImage = async (e, index, parcelNo) => {
        e.stopPropagation(); // สำคัญ: หยุดไม่ให้ Collapse พับ/กางตอนกดปุ่ม

        const elementId = `capture-node-${index}`; // อ้างอิง ID ที่เราจะสร้างในข้อ 4
        const element = document.getElementById(elementId);

        if (!element) {
            message.warning('กรุณาเปิดแถบข้อมูลเพื่อแสดงรายละเอียดก่อนบันทึกภาพ');
            return;
        }

        const hideLoading = message.loading('กำลังบันทึกภาพ...', 0);

        try {
            const canvas = await html2canvas(element, {
                scale: 2, // เพิ่มความชัด
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false
            });

            const image = canvas.toDataURL("image/png");
            const link = document.createElement('a');
            link.href = image;
            link.download = `Tracking_${parcelNo}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            hideLoading();
            message.success('บันทึกรูปภาพเรียบร้อย');
        } catch (error) {
            console.error('Capture Error:', error);
            hideLoading();
            message.error('ไม่สามารถบันทึกภาพได้');
        }
    };

    return (
        <>
            <Modal
                open={open}
                onCancel={handleClose}
                width={'90%'}
                // width={1100}
                title="ติดตามสถานะไปรษณีย์ไทย"
                footer={null}
                centered
            >
                <Spin spinning={loading} tip="กำลังโหลดข้อมูล..." size="large">
                    <Card size="small" style={{ marginBottom: '20px', borderRadius: '8px', background: '#f9f9f9' }}>
                        <Row gutter={[16, 16]} align="middle">
                            <Col span={24}>
                                <Space wrap size={[12, 12]}>
                                    <Tag
                                        color={'blue'}
                                        style={{ cursor: 'pointer', padding: '5px 12px' }}
                                        onClick={() => setFilterStatus('all')}
                                    >
                                        <b>ทั้งหมด ({summary.all})</b>
                                    </Tag>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {/* เพิ่ม 000 เข้าไปในปุ่มกรองด่วนด้วย */}
                                        {['501', '401', '000'].map(code => (
                                            <Tag
                                                key={code}
                                                color={getStatusColor(code)}
                                                style={{ cursor: 'pointer', padding: '5px 12px' }}
                                                onClick={() => setFilterStatus(code)}
                                            >
                                                <span style={{ fontWeight: 'bold' }}>
                                                    {STATUS_LABELS[code]}
                                                    <span style={{ marginLeft: '8px', opacity: 0.8 }}>
                                                        ({summary[code] || 0})
                                                    </span>
                                                </span>
                                            </Tag>
                                        ))}
                                    </div>
                                </Space>
                            </Col>
                            <Col span={24}>
                                <Select
                                    value={filterStatus}
                                    style={{ width: '100%' }}
                                    onChange={setFilterStatus}
                                    showSearch
                                    optionFilterProp="label"
                                >
                                    <Select.Option value="all" label="ทั้งหมด">ทั้งหมด</Select.Option>
                                    {Object.entries(STATUS_LABELS).map(([code, label]) => (
                                        code !== 'all' && <Select.Option key={code} value={code} label={label}>{label}</Select.Option>
                                    ))}
                                </Select>
                            </Col>
                        </Row>
                    </Card>

                    {chunkedData?.parcelChunks?.length > 0 ? (
                        <Card size="small" style={{ marginBottom: '20px', borderRadius: '8px', background: '#f9f9f9' }}>
                            <div style={{ marginBottom: 10 }}>
                                <Title level={5}>คัดลอกเลขพัสดุ (ชุดละ 300 รายการ)</Title>
                                <Space wrap>
                                    {chunkedData.parcelChunks.map((chunk, index) => (
                                        <Button
                                            key={`chunk-${index}`}
                                            icon={<CopyOutlined />}
                                            onClick={() => copyToClipboard(chunk.join(',\n'), `ชุดที่ ${index + 1}`)}
                                        >
                                            คัดลอกชุดที่ {index + 1} ({chunk.length})
                                        </Button>
                                    ))}
                                </Space>
                            </div>
                        </Card>
                    ) : null}

                    {filteredData.length > 0 ? (
                        <Collapse accordion defaultActiveKey={['0']}>
                            {filteredData.map((item, index) => (
                                <Panel
                                    key={index}
                                    header={
                                        // --- ส่วน Header ตามโค้ดที่คุณส่งมา ---
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                            <Col span={12} style={{ textAlign: "start" }}>
                                                <b>สัญญา: {item?.CONTNO}
                                                    <Tag style={{ marginLeft: 8 }} color={getStatusColor(item.statusCode)}>
                                                        {item.statusDescription}
                                                    </Tag>
                                                </b>
                                                <br />
                                                <Tag style={{ marginTop: "5px" }} color='blue'>{item.parcelNo}</Tag>
                                                {item.parcelNoResponse && (
                                                    <> / <Tag color='blue'>{item.parcelNoResponse}</Tag></>
                                                )}
                                            </Col>
                                            <Col span={12} style={{ textAlign: "end" }}>
                                                <Space>
                                                    <Button
                                                        type='primary'
                                                        icon={<PictureOutlined />} // อย่าลืม Import PictureOutlined หรือ DownloadOutlined
                                                        style={{ backgroundColor: 'orange' }}
                                                        onClick={(e) => handleCaptureImage(e, index, item.parcelNo)}
                                                    >บันทึกรูปภาพ</Button>
                                                    <Button
                                                        type='primary'
                                                        icon={<PlusOutlined />}
                                                        style={{ backgroundColor: 'green' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleUploadIMGPOST(item);
                                                        }}
                                                    >เพิ่มข้อมูล</Button>
                                                </Space>
                                            </Col>
                                        </div>
                                    }
                                >
                                    {/* เรียกใช้ Component ที่แยกไฟล์ออกไป */}
                                    <TrackingDetail item={item} index={index} />

                                </Panel>
                            ))}
                        </Collapse>
                    ) : (
                        <Empty style={{ padding: '40px' }} description="ไม่พบข้อมูลพัสดุ" />
                    )}

                    <Row>
                        <Col span={24} style={{ textAlign: "end", marginTop: "15px" }}>
                            <Button
                                type='primary'
                                style={{ backgroundColor: 'green' }}
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                disabled={filteredData.length === 0}
                            >
                                สร้างการคัดลอก
                            </Button>
                        </Col>
                    </Row>
                </Spin>
                {
                    isModalUploadImg ?
                        <UploadIMGPOST
                            open={isModalUploadImg}
                            close={setIsModalUploadImg}
                            data={modalUploadImgData}
                            setData={setModalUploadImgData}
                        />
                        : null
                }
            </Modal >
        </>
    );
}

export default CheckThaiPost;